import React, { useState, useRef } from 'react';
import { X, Download, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';

const ImagePreviewModal = ({ isOpen, onClose, imageUrl, imageName }) => {
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const imageRef = useRef(null);

    React.useEffect(() => {
        if (isOpen) {
            setZoom(1);
            setRotation(0);
            setImageLoaded(false);
            setDragOffset({ x: 0, y: 0 });
            setIsDragging(false);
        }
    }, [isOpen]);

    const handleDownload = async () => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = imageName || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
    const handleRotate = () => setRotation(prev => (prev + 90) % 360);

    const resetView = () => {
        setZoom(1);
        setRotation(0);
        setDragOffset({ x: 0, y: 0 });
    };

    const handleMouseDown = (e) => {
        if (zoom > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && zoom > 1) {
            setDragOffset({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Touch events for mobile support
    const handleTouchStart = (e) => {
        if (zoom > 1 && e.touches.length === 1) {
            const touch = e.touches[0];
            setIsDragging(true);
            setDragStart({
                x: touch.clientX - dragOffset.x,
                y: touch.clientY - dragOffset.y
            });
        }
    };

    const handleTouchMove = (e) => {
        if (isDragging && zoom > 1 && e.touches.length === 1) {
            e.preventDefault();
            const touch = e.touches[0];
            setDragOffset({
                x: touch.clientX - dragStart.x,
                y: touch.clientY - dragStart.y
            });
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center backdrop-blur-sm"
            onClick={onClose}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
        >
            {/* Header */}
           <div className="absolute top-0 left-0 right-0 z-[60] bg-gradient-to-b from-black/80 to-transparent p-4">
  <div className="flex items-center justify-between text-white">
    <div className="flex items-center space-x-4">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
        className="p-2 hover:bg-white/20 rounded-full transition-colors z-[70]"
      >
        <X className="w-6 h-6" />
      </button>
      <span className="text-lg font-medium truncate max-w-xs">
        {imageName || 'Image'}
      </span>
    </div>

                   <div className="flex items-center space-x-2 z-[70]">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleZoomOut();
        }}
        className="p-2 hover:bg-white/20 rounded-full transition-colors"
        title="Zoom Out"
      >
        <ZoomOut className="w-5 h-5" />
      </button>
      <span className="text-sm px-3 py-1 bg-black/60 rounded min-w-[60px] text-center">
        {Math.round(zoom * 100)}%
      </span>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleZoomIn();
        }}
        className="p-2 hover:bg-white/20 rounded-full transition-colors"
        title="Zoom In"
      >
        <ZoomIn className="w-5 h-5" />
      </button>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleRotate();
        }}
        className="p-2 hover:bg-white/20 rounded-full transition-colors"
        title="Rotate"
      >
        <RotateCw className="w-5 h-5" />
      </button>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          resetView();
        }}
        className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm transition-colors"
      >
        Reset
      </button>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDownload();
        }}
        className="p-2 bg-teal-600 hover:bg-teal-500 rounded-full transition-colors"
        title="Download"
      >
        <Download className="w-5 h-5" />
      </button>
    </div>
  
                </div>
            </div>

            {/* Image Container */}
            <div
                className="relative w-full h-full flex items-center justify-center p-20"
                onClick={(e) => e.stopPropagation()}
            >
                <img
                    ref={imageRef}
                    src={imageUrl}
                    alt={imageName || 'Preview'}
                    className={`max-w-[70vw] max-h-[70vh] object-contain transition-all duration-300 select-none ${
                        zoom > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
                    } ${isDragging ? 'cursor-grabbing' : ''}`}
                    style={{
                        transform: `scale(${zoom}) rotate(${rotation}deg) translate(${dragOffset.x}px, ${dragOffset.y}px)`,
                        transformOrigin: 'center'
                    }}
                    draggable={false}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                />
            </div>

            {/* Bottom gradient for mobile */}
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
        </div>
    );
};

export default ImagePreviewModal;